import { Component } from '@theme/component';

/**
 * Drives the "I CARE" water-fill story sequence:
 * idle -> filling with water -> filling with additives -> revealed.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} liquid
 * @property {HTMLElement} cta
 * @property {HTMLElement} stage
 * @property {HTMLElement} bottle
 * @property {HTMLElement} band
 *
 * @extends {Component<Refs>}
 */
class StoryWaterFill extends Component {
  requiredRefs = ['liquid', 'cta', 'stage', 'bottle', 'band'];

  /** @type {'idle' | 'filling-water' | 'filling-additives' | 'done'} */
  #state = 'idle';

  /** @type {number[]} */
  #fillIds = [];

  /** @type {number | null} */
  #kickRafId = null;

  /** @type {number | null} */
  #kickFallbackId = null;

  /** @type {number | null} */
  #potDelayId = null;

  /** @type {number | null} */
  #potHoldId = null;

  /** @type {number | null} */
  #benefitsDelayId = null;

  /** @type {number | null} */
  #soapHoldId = null;

  /** @type {number | null} */
  #recipesDelayId = null;

  /** @type {number[]} */
  #benefitsIds = [];

  /** @type {number[]} */
  #recipesIds = [];

  /** @type {number | null} */
  #bundleId = null;

  connectedCallback() {
    super.connectedCallback();
    this.style.setProperty('--fill', '0%');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#fillIds.forEach((id) => clearTimeout(id));
    this.#fillIds = [];
    if (this.#kickRafId !== null) cancelAnimationFrame(this.#kickRafId);
    if (this.#kickFallbackId !== null) clearTimeout(this.#kickFallbackId);
    if (this.#potDelayId !== null) clearTimeout(this.#potDelayId);
    if (this.#potHoldId !== null) clearTimeout(this.#potHoldId);
    if (this.#benefitsDelayId !== null) clearTimeout(this.#benefitsDelayId);
    if (this.#soapHoldId !== null) clearTimeout(this.#soapHoldId);
    if (this.#recipesDelayId !== null) clearTimeout(this.#recipesDelayId);
    this.#benefitsIds.forEach((id) => clearTimeout(id));
    this.#benefitsIds = [];
    this.#recipesIds.forEach((id) => clearTimeout(id));
    this.#recipesIds = [];
    if (this.#bundleId !== null) clearTimeout(this.#bundleId);
  }

  /**
   * Kicks off the sequence when the "I CARE" button is pressed.
   * @param {Event} event
   */
  start(event) {
    event?.preventDefault();
    if (this.#state !== 'idle') return;

    this.#state = 'filling-water';
    this.classList.add('is-started');
    this.refs.cta.setAttribute('aria-hidden', 'true');
    this.refs.cta.tabIndex = -1;

    const waterPercent = Number(this.dataset.waterPercent || 80);
    const captionHoldMs = Number(this.dataset.captionHold || 1600);
    const endHoldMs = Number(this.dataset.endHold || 1400);
    const revealHoldMs = Number(this.dataset.revealHold || 3000);
    const captionFadeMs = 500;

    this.#showCaption('we-do-too');
    setTimeout(() => this.#hideCaption('we-do-too'), captionHoldMs);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const waterMs = reduceMotion ? 0 : Number(this.dataset.waterDuration || 4000);
    const additiveMs = reduceMotion ? 0 : Number(this.dataset.additiveDuration || 800);
    const waterCaptionMs = waterPercent > 0 ? waterMs * (50 / waterPercent) : waterMs;

    requestAnimationFrame(() => {
      this.style.setProperty('--fill', `${waterPercent}%`);
    });

    this.#fillIds.push(
      setTimeout(() => {
        this.#showCaption('water');
      }, waterCaptionMs)
    );

    this.#fillIds.push(
      setTimeout(() => {
        this.#state = 'filling-additives';
        this.refs.liquid.style.transitionDuration = 'var(--story-additive-duration, 0.8s)';
        this.style.setProperty('--fill', '100%');

        this.#fillIds.push(
          setTimeout(() => {
            this.#state = 'done';
            this.classList.add('is-filled');
            this.#showCaption('additive');
            setTimeout(() => {
              this.#showCaption('reveal');
              setTimeout(() => {
                this.#hideCaption('reveal');
                setTimeout(() => {
                  this.#showCaption('crazy');
                  setTimeout(() => this.#revealYes(), 1000);
                }, captionFadeMs);
              }, revealHoldMs);
            }, endHoldMs);
          }, additiveMs)
        );
      }, waterMs)
    );
  }

  /**
   * YES click: flatten the wave, then zoom out so the fill is revealed
   * as the inside of the body-wash bottle.
   * @param {Event} event
   */
  onYes(event) {
    event?.preventDefault();
    if (this.#state !== 'done' || this.classList.contains('is-zooming')) return;

    this.querySelectorAll('.story-water-fill__caption.is-visible').forEach((el) => {
      el.classList.remove('is-visible');
    });
    this.classList.add('is-flattening');

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      this.#beginZoom(true);
      return;
    }

    setTimeout(() => this.#beginZoom(false), 380);
  }

  /** @param {boolean} reduceMotion */
  #beginZoom(reduceMotion) {
    const { bottle, band, stage } = this.refs;

    if (reduceMotion) {
      this.classList.add('is-zooming', 'is-zoomed-out');
      this.#showAfterZoom(true);
      return;
    }

    const waterPct = Number(this.dataset.waterPercent || 80) / 100;
    const inset = 0.8;
    bottle.style.transition = 'none';
    bottle.style.transform = 'none';

    const stageR = stage.getBoundingClientRect();
    const bottleR = bottle.getBoundingClientRect();
    const bandR = band.getBoundingClientRect();
    const scale = Math.max(stageR.width / (bandR.width * inset), stageR.height / bandR.height);
    const bandCx = bandR.left + bandR.width / 2 - bottleR.left;
    const seamY = bandR.top + bandR.height * (1 - waterPct) - bottleR.top;
    const tx = stageR.left + stageR.width / 2 - bottleR.left - bandCx * scale;
    const ty = stageR.top + stageR.height * (1 - waterPct) - bottleR.top - seamY * scale;

    bottle.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    // Show the crop only after it already covers the viewport, so we don't
    // flash the resting bottle.
    this.classList.add('is-zooming');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-zoomed-out');
        bottle.style.transition = 'transform 1.25s cubic-bezier(0.22, 0.7, 0.2, 1)';
        bottle.style.transform = 'none';
        setTimeout(() => this.#showAfterZoom(false), 1250);
      });
    });
  }

  /** After the bottle lands, fade in the first two lines, then LOTS OF BOTTLES,
   * scatter extra bottles, pop them away, then the "in other words" caption.
   * @param {boolean} reduceMotion
   */
  #showAfterZoom(reduceMotion) {
    const caption = this.querySelector('[data-caption="plastic"]');
    if (!caption) return;

    caption.classList.add('is-visible');
    if (reduceMotion) {
      caption.classList.add('is-lots-visible');
      caption.classList.remove('is-visible');
      this.#showWords(true);
      return;
    }

    const delay = Number(this.dataset.lotsDelay || 3000);
    setTimeout(() => {
      caption.classList.add('is-lots-visible');
      setTimeout(() => {
        this.classList.add('is-scattered');
        const hold = Number(this.dataset.scatterHold || 2000);
        setTimeout(() => this.#popScatter(), 1250 + hold);
      }, 480);
    }, delay);
  }

  /** Extra bottles pop away, then the next caption appears. */
  #popScatter() {
    const plastic = this.querySelector('[data-caption="plastic"]');
    this.classList.add('is-unscattering');
    if (plastic) plastic.classList.remove('is-visible');
    setTimeout(() => {
      this.classList.remove('is-scattered');
      this.#showWords(false);
    }, 900);
  }

  /** @param {boolean} reduceMotion */
  #showWords(reduceMotion) {
    const caption = this.querySelector('[data-caption="words"]');
    if (!caption) return;

    caption.classList.add('is-visible');
    if (reduceMotion) {
      caption.classList.add('is-words-complete');
      this.#revealKick();
      return;
    }

    const delay = Number(this.dataset.wordsDelay || 2500);
    setTimeout(() => {
      caption.classList.add('is-words-complete');
      setTimeout(() => this.#revealKick(), 600);
    }, delay);
  }

  /** Fades in KICK THE BOTTLE, then draws the pointing arrow as a marker stroke. */
  #revealKick() {
    const kick = this.querySelector('.story-water-fill__kick');
    if (!kick) return;

    kick.classList.add('is-visible');
    kick.removeAttribute('aria-hidden');
    kick.tabIndex = 0;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const draw = kick.querySelector('.story-water-fill__kick-arrow-draw animate');
    if (reduceMotion || !draw || typeof draw.beginElement !== 'function') {
      kick.classList.add('is-drawn');
      return;
    }

    setTimeout(() => {
      try {
        draw.beginElement();
      } catch (error) {
        kick.classList.add('is-drawn');
      }
    }, 500);
  }

  /**
   * KICK click: a foot swings down from above the bottle and boots it off-screen.
   * @param {Event} event
   */
  onKick(event) {
    event?.preventDefault();
    const kick = this.querySelector('.story-water-fill__kick');
    if (!kick?.classList.contains('is-visible')) return;
    if (this.classList.contains('is-kicking') || this.classList.contains('is-kicked')) return;

    kick.classList.remove('is-visible');
    kick.setAttribute('aria-hidden', 'true');
    kick.tabIndex = -1;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      this.classList.add('is-kicked');
      this.#revealCare(true);
      return;
    }

    this.classList.add('is-kicking');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#watchKickContact());
    });
  }

  /** Starts the bottle fly on the first frame the shoe overlaps the bottle. */
  #watchKickContact() {
    const hit = this.querySelector('.story-water-fill__foot-hit');
    const bottle = this.refs.bottle;
    if (!hit || !bottle) {
      this.#onKickContact();
      return;
    }

    this.#kickFallbackId = window.setTimeout(() => this.#onKickContact(), 560);

    const tick = () => {
      if (this.classList.contains('is-kick-contact') || this.classList.contains('is-kicked')) return;

      const a = hit.getBoundingClientRect();
      const b = bottle.getBoundingClientRect();
      const touching = a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

      if (touching) {
        this.#onKickContact();
        return;
      }

      this.#kickRafId = requestAnimationFrame(tick);
    };

    this.#kickRafId = requestAnimationFrame(tick);
  }

  #onKickContact() {
    if (this.classList.contains('is-kick-contact') || this.classList.contains('is-kicked')) return;

    if (this.#kickFallbackId !== null) {
      clearTimeout(this.#kickFallbackId);
      this.#kickFallbackId = null;
    }
    if (this.#kickRafId !== null) {
      cancelAnimationFrame(this.#kickRafId);
      this.#kickRafId = null;
    }

    const foot = this.querySelector('.story-water-fill__foot');
    this.classList.add('is-kick-contact');
    if (foot) foot.style.animationPlayState = 'paused';

    window.setTimeout(() => {
      if (foot) foot.style.animationPlayState = 'running';
      window.setTimeout(() => {
        this.classList.add('is-kicked');
        this.#revealCare(false);
      }, 720);
    }, 70);
  }

  /**
   * After the bottle is gone: rows 1+2, then 3, then 4, then the closer, then WHAT WE DID.
   * @param {boolean} reduceMotion
   */
  #revealCare(reduceMotion) {
    const caption = this.querySelector('[data-caption="care"]');
    if (!caption) return;

    caption.classList.add('is-visible');
    if (reduceMotion) {
      caption.classList.add('is-care-3', 'is-care-4', 'is-care-hands');
      this.#revealDid();
      return;
    }

    const firstDelay = Number(this.dataset.careFirstDelay || 4000);
    const delay = Number(this.dataset.careDelay || 2000);
    const closerDelay = Number(this.dataset.careHandsDelay || 2000);
    setTimeout(() => {
      caption.classList.add('is-care-3');
      setTimeout(() => {
        caption.classList.add('is-care-4');
        setTimeout(() => {
          caption.classList.add('is-care-hands');
          setTimeout(() => this.#revealDid(), closerDelay);
        }, closerDelay);
      }, delay);
    }, firstDelay);
  }

  /** Fades in WHAT WE DID, then draws the pointing arrow as a marker stroke. */
  #revealDid() {
    const did = this.querySelector('.story-water-fill__did');
    if (!did) return;

    did.classList.add('is-visible');
    did.removeAttribute('aria-hidden');
    did.tabIndex = 0;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const draw = did.querySelector('.story-water-fill__did-arrow-draw animate');
    if (reduceMotion || !draw || typeof draw.beginElement !== 'function') {
      did.classList.add('is-drawn');
      return;
    }

    setTimeout(() => {
      try {
        draw.beginElement();
      } catch (error) {
        did.classList.add('is-drawn');
      }
    }, 500);
  }

  /**
   * WHAT WE DID click: hide the care beat, wait, then show mixed-oils
   * and the pot with falling drops. After a hold, switch to benefits.
   * @param {Event} event
   */
  onDid(event) {
    event?.preventDefault();
    const did = this.querySelector('.story-water-fill__did');
    if (!did?.classList.contains('is-visible') || this.classList.contains('is-mixing')) return;

    this.classList.add('is-mixing');
    did.classList.remove('is-visible');
    did.setAttribute('aria-hidden', 'true');
    did.tabIndex = -1;
    this.#hideCaption('care');

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delayMs = Number(this.dataset.potDelay || 1200);
    if (reduceMotion || delayMs <= 0) {
      this.#revealPot(reduceMotion);
      return;
    }

    this.#potDelayId = setTimeout(() => this.#revealPot(reduceMotion), delayMs);
  }

  /** @param {boolean} reduceMotion */
  #revealPot(reduceMotion) {
    const caption = this.querySelector('[data-caption="pot"]');
    if (!caption) return;

    caption.classList.add('is-visible');

    const holdMs = Number(this.dataset.potHold || 5000);
    const showBenefits = () => {
      this.#hideCaption('pot');
      const gapMs = Number(this.dataset.benefitsDelay || 1200);
      if (reduceMotion || gapMs <= 0) {
        this.#revealBenefits(reduceMotion);
        return;
      }
      this.#benefitsDelayId = setTimeout(() => this.#revealBenefits(reduceMotion), gapMs);
    };

    if (reduceMotion) {
      caption.classList.add('is-pot-illust');
      this.#potHoldId = setTimeout(showBenefits, holdMs);
      return;
    }

    setTimeout(() => {
      caption.classList.add('is-pot-illust');
      this.#potHoldId = setTimeout(showBenefits, holdMs);
    }, 1600);
  }

  /** @param {boolean} reduceMotion */
  #revealBenefits(reduceMotion) {
    const caption = this.querySelector('[data-caption="benefits"]');
    if (!caption) return;

    this.#benefitsIds.forEach((id) => clearTimeout(id));
    this.#benefitsIds = [];
    if (this.#soapHoldId !== null) clearTimeout(this.#soapHoldId);

    caption.classList.add('is-visible');

    const lineSteps = ['is-benefits-2', 'is-benefits-3', 'is-benefits-4'];
    const soapHold = Number(this.dataset.soapHold || 3000);
    const showRecipes = () => {
      this.#hideCaption('benefits');
      const gapMs = Number(this.dataset.recipesDelay || 1200);
      if (reduceMotion || gapMs <= 0) {
        this.#revealRecipes(reduceMotion);
        return;
      }
      this.#recipesDelayId = setTimeout(() => this.#revealRecipes(reduceMotion), gapMs);
    };

    if (reduceMotion) {
      caption.classList.add(...lineSteps, 'is-benefits-soap');
      this.#soapHoldId = setTimeout(showRecipes, soapHold);
      return;
    }

    const stagger = Number(this.dataset.benefitsStagger || 1500);
    const soapAppear = Number(this.dataset.soapAppear || 1500);
    lineSteps.forEach((cls, index) => {
      this.#benefitsIds.push(setTimeout(() => caption.classList.add(cls), stagger * (index + 1)));
    });
    const soapAt = stagger * lineSteps.length + soapAppear;
    this.#benefitsIds.push(setTimeout(() => caption.classList.add('is-benefits-soap'), soapAt));
    this.#soapHoldId = setTimeout(showRecipes, soapAt + soapHold);
  }

  /** @param {boolean} reduceMotion */
  #revealRecipes(reduceMotion) {
    const caption = this.querySelector('[data-caption="recipes"]');
    if (!caption) return;

    this.#recipesIds.forEach((id) => clearTimeout(id));
    this.#recipesIds = [];
    if (this.#bundleId !== null) {
      clearTimeout(this.#bundleId);
      this.#bundleId = null;
    }

    caption.classList.add('is-visible');

    const steps = ['is-recipes-3', 'is-recipes-4'];
    const stagger = Number(this.dataset.recipesStagger || 3000);
    const bundleDelay = Number(this.dataset.bundleDelay || 2000);

    if (reduceMotion) {
      caption.classList.add(...steps);
      this.#bundleId = setTimeout(() => this.#showBundle(), bundleDelay);
      return;
    }

    steps.forEach((cls, index) => {
      this.#recipesIds.push(setTimeout(() => caption.classList.add(cls), stagger * (index + 1)));
    });
    this.#bundleId = setTimeout(() => this.#showBundle(), stagger * steps.length + bundleDelay);
  }

  #showBundle() {
    if (!this.querySelector('[data-story-bundle]')) return;
    this.classList.add('is-bundle');
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    });
  }

  /** Fades in YES, then draws the pointing arrow as a marker stroke. */
  #revealYes() {
    const yes = this.querySelector('.story-water-fill__yes');
    if (!yes) return;

    yes.classList.add('is-visible');

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const draw = yes.querySelector('.story-water-fill__yes-arrow-draw animate');
    if (reduceMotion || !draw || typeof draw.beginElement !== 'function') {
      yes.classList.add('is-drawn');
      return;
    }

    setTimeout(() => {
      try {
        draw.beginElement();
      } catch (error) {
        yes.classList.add('is-drawn');
      }
    }, 500);
  }

  /** @param {string} name */
  #showCaption(name) {
    this.querySelector(`[data-caption="${name}"]`)?.classList.add('is-visible');
  }

  /** @param {string} name */
  #hideCaption(name) {
    this.querySelector(`[data-caption="${name}"]`)?.classList.remove('is-visible');
  }
}

if (!customElements.get('story-water-fill')) {
  customElements.define('story-water-fill', StoryWaterFill);
}
